import { Dialog } from '@reach/dialog';
import '@reach/dialog/styles.css';
import CameraPhoto, { FACING_MODES } from 'jslib-html5-camera-photo';
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from '@ui/atoms/Button';
import TextButton, { IButtonProps } from '@ui/atoms/TextButton';

import { processImage } from '@utils/image';

import Camera from '@assets/vectors/camera.inline.svg';
import Checkmark from '@assets/vectors/checkmark.inline.svg';
import Close from '@assets/vectors/close.inline.svg';

import theme from '@theme/FolxTheme';

interface IPhotoTakerProps extends IButtonProps {
	isMobile: boolean;
	handleTakePhoto?: (image: string) => void;
	photoRef?: any;
}

const StyledDialog = styled(Dialog)`
	align-items: center;
	display: flex;
	flex-direction: column;
	justify-content: stretch;
	max-width: 72rem;
	padding: ${theme.spacing.lg};
	width: 80vw;
	z-index: ${theme.zIndexes.foreground};
`;

const CameraFeed = styled.video`
	margin-bottom: ${theme.spacing.lg};
	transform: rotateY(180deg);
	max-height: 85vh;
	width: 100%;
`;

const CameraImage = styled.img`
	margin-bottom: ${theme.spacing.lg};
	transform: rotateY(180deg);
	object-fit: contain;
	max-height: 85vh;
	width: 100%;
`;

const ButtonRow = styled.div`
	align-items: stretch;
	display: flex;
	justify-content: flex-end;
	width: 100%;
`;

const Group = styled.div`
	align-items: center;
	display: flex;
	flex-basis: 33%;
	justify-content: center;
`;

const RightGroup = styled(Group)`
	justify-content: flex-end;
`;

const CameraControlsButton = styled(Button)`
	border-radius: 50%;
	padding: ${theme.spacing.sm};
	width: fit-content;

	svg {
		height: 3rem;
		width: 3rem;

		path {
			fill: ${theme.colors.white};
		}
	}

	:focus:not(:focus-visible) {
		outline: none;
	}
`;

const InverseControlsButton = styled(CameraControlsButton)`
	margin-left: ${theme.spacing.sm};

	svg > path {
		fill: ${theme.colors.white};
	}
`;

const CancelButton = styled(TextButton)`
	height: fit-content;
	padding-right: ${theme.spacing.sm};
`;

const PhotoTaker: FC<IPhotoTakerProps> = ({
	color = 'cyan',
	handleTakePhoto = () => {},
	isMobile,
	label,
	photoRef,
	tint = 'darken40',
	underline,
	variant = 'text',
}) => {
	const videoRef = useRef<HTMLVideoElement>();

	const [camera, setCamera] = useState(null);
	const [cameraSupported, setCameraSupported] = useState(false);
	const [initializeCamera, setInitializeCamera] = useState(false);
	const [showDialog, setShowDialog] = useState(false);
	const [userPhoto, setUserPhoto] = useState(null);
	const [processedImage, setProcessedImage] = useState('');

	const open = () => setShowDialog(true);
	const close = () => setShowDialog(false);

	const stopCamera = (camera) => {
		camera.stopCamera().catch((error) => {
			console.log(error);
		});
	};

	useEffect(() => {
		navigator?.mediaDevices
			?.getUserMedia({ video: true })
			.then((stream) => {
				setCameraSupported(true);
				stream.getTracks().forEach((track) => track.stop());
			})
			.catch((e) => console.log(e));
	}, []);

	useEffect(() => {
		initializeCamera && !userPhoto && setCamera(new CameraPhoto(videoRef.current));
	}, [initializeCamera, userPhoto]);

	useEffect(() => {
		if (!showDialog) {
			camera && stopCamera(camera);
			setInitializeCamera(false);
		}
	}, [showDialog]);

	useEffect(() => {
		if (!camera) return;
		const idealResolution = { width: 640, height: 480 };
		const facingMode = FACING_MODES.USER;

		if (showDialog) {
			camera.startCamera(facingMode, idealResolution).catch((error) => {
				console.log(error);
				camera && stopCamera(camera);
			});
		}

		return () => {
			camera && stopCamera(camera);
		};
	}, [camera]);

	useEffect(() => {
		if (!userPhoto) {
			setProcessedImage('');
			return;
		}

		processImage(userPhoto).then((image) => {
			setProcessedImage(image);
		});

		return () => {
			camera && stopCamera(camera);
		};
	}, [userPhoto]);

	const showTakePhoto = !isMobile && cameraSupported;

	return (
		<div>
			{showTakePhoto && (
				<TextButton
					color={color}
					label={label}
					tint={tint}
					variant={variant}
					underline={underline}
					onClick={open}
				/>
			)}
			<StyledDialog isOpen={showDialog} onDismiss={close} onFocus={() => setInitializeCamera(true)}>
				{userPhoto ? (
					<CameraImage src={userPhoto} alt="photo" />
				) : (
					<CameraFeed ref={videoRef} autoPlay />
				)}
				<ButtonRow>
					<Group>
						{userPhoto ? (
							<>
								<CameraControlsButton
									label={<Checkmark />}
									variant="secondary"
									onClick={() => {
										handleTakePhoto(processedImage);
										close();
									}}
								/>
								<InverseControlsButton label={<Close />} onClick={() => setUserPhoto(null)} />
							</>
						) : (
							<CameraControlsButton
								label={<Camera />}
								variant="secondary"
								onClick={() => {
									const dataUri = camera.getDataUri({
										sizeFactor: 1,
									});
									setUserPhoto(dataUri);
								}}
							/>
						)}
					</Group>
					<RightGroup>
						<CancelButton
							color="cyan"
							label="Cancel"
							tint="darken40"
							variant="underline"
							onClick={() => {
								setUserPhoto(null);
								close();
							}}
						/>
					</RightGroup>
				</ButtonRow>
			</StyledDialog>
			<input type="hidden" id="userImage" name="userImage" value={processedImage} ref={photoRef} />
		</div>
	);
};

export default PhotoTaker;
